import React, { useEffect, useState } from 'react';
import useAolSaveHandler from '../../../../app/useAolSaveHandler';
import { BoilersUsageTypeEnum, BoilerTypeEnumV3, EngineDataTypeEnum, NoonReportSectionEnum } from '../../../../types/ViewModel/AolEnums';
import { AolDataItem } from '../../../../types/ViewModel/AolItem';
import { Vessel } from '../../../../types/ViewModel/Vessel/Vessel';
import {
    getConsumptionCost,
    getConsumptionCostByFuelType,
    getNumberValue,
    hasInvalidFields,
    hasMissingValues,
} from '../../../../utils/aolFieldsHelper';
import { ErrorText } from '../../../shared/error-text/ErrorText.style';
import { ListHeaderStyle, SectionInnerWrapper, SubSectionHeader } from '../../aol-container/AolContainer.style';
import OdfjellInputComponent, { InputChangeData, TimeInputChangeData } from '../../../common/odfjell-input-component/OdfjellInputComponent';
import { EnginesDataVM } from '../EnginesSection';
import { EngineListHeader, EngineListRow, EngineListValuesWrapper, EngineListWrapper } from '../EnginesSection.style';
import { roundTo2Decimals } from '../../../../utils/numberHelper';
import useAolItemsHelper from '../../../../utils/aol/useAolItemsHelper';
import ConsumptionContainer from '../../consumption/ConsumptionContainer';
import { Consumption } from 'src/types/ViewModel/Aol/Consumption';
import { FuelType } from 'src/types/ViewModel/Bunkering/FuelType';
import ConsumptionByUsage from './ConsumptionByUsage';

type Props = {
    enginesDataVM: EnginesDataVM;
    onChange: () => void;
    vessel: Vessel | null;
    aolDataItem: AolDataItem;
    hasEmptyValues;
    isReadOnly: boolean;
    addConsumptionRow: (consumption: Consumption[]) => void;
};

const BoilersSectionComponent: React.FC<Props> = ({
    enginesDataVM,
    onChange,
    vessel,
    aolDataItem,
    hasEmptyValues,
    isReadOnly,
    addConsumptionRow,
}) => {
    const [canUseHfo, setCanUseHfo] = useState(false);
    const [hasCompositeBoiler, setHasCompositeBoiler] = useState(false);
    const [numberOfAuxBoilers, setNumberOfAuxBoilers] = useState(2);
    const [hasEmptyValuesLocal, setHasEmptyValuesLocal] = useState(false);
    const [isBoilerConsumptionHFOSumWrong, setIsBoilerConsumptionHFOSumWrong] = useState(false);
    const [isBoilerConsumptionMGOSumWrong, setIsBoilerConsumptionMGOSumWrong] = useState(false);
    const [invalidBoilerConsumptionByUsage, setInvalidBoilerConsumptionByUsage] = useState<FuelType[]>([]);
    const [isBoilerConsumptionTankPreparationIncorrect, setIsBoilerConsumptionTankPreparationIncorrect] = useState(false);
    const { saveAolEvent } = useAolSaveHandler();
    const [canProduceFreshWaterWithSteam, setCanProduceFreshWaterWithSteam] = useState(false);
    const [isRunningHoursInconsistent, setIsRunningHoursInconsistent] = useState(false);
    const { getVoyageFuelPrice } = useAolItemsHelper();
    const [consumptionTypes, setConsumptionTypes] = useState<FuelType[]>([]);

    useEffect(() => {
        if (!vessel?.vesselConfigurationV3) return;
        initDataVessel(vessel);
    }, [vessel]);

    useEffect(() => {
        setIsBoilerConsumptionTankPreparationIncorrect(checkBoilerConsumptionTankPreparation());
    }, [aolDataItem?.aolDataItemProperties.hasTankPreparationAolData]);

    useEffect(() => {
        if (
            saveAolEvent &&
            saveAolEvent.type === 'save_section' &&
            saveAolEvent.section === NoonReportSectionEnum.Engines &&
            aolDataItem &&
            saveAolEvent.aolDataItem.id === aolDataItem.id
        ) {
            checkOnSave();
        }
    }, [saveAolEvent]);

    useEffect(() => {
        const consumptionFuelTypes =
            enginesDataVM.consumption?.filter((c) => c.engineDataType === EngineDataTypeEnum.Boilers)?.map((c) => c.fuelType) || [];
        const boilerV5FuelTypes = enginesDataVM.boilerConsumptionV5?.map((b) => b.FuelTypeId) || [];
        const allFuelTypes = [...consumptionFuelTypes, ...boilerV5FuelTypes];
        const distinctFuelTypes = Array.from(new Set(allFuelTypes));

        setConsumptionTypes(distinctFuelTypes);
    }, [enginesDataVM]);

    const initDataVessel = (vessel: Vessel) => {
        const auxBoiler = vessel.vesselConfigurationV3!.engineConfiguration.boilers.find((b) => b.boilerType === BoilerTypeEnumV3.Auxiliary);
        const numberOfAuxBoilers = vessel.vesselConfigurationV3!.engineConfiguration.boilers.filter(
            (b) => b.boilerType === BoilerTypeEnumV3.Auxiliary || b.boilerType === BoilerTypeEnumV3.Donkey
        ).length;
        const hasCompositeBoiler = vessel.vesselConfigurationV3!.engineConfiguration.boilers.some((b) => b.boilerType === BoilerTypeEnumV3.Composite);

        setCanUseHfo(auxBoiler?.canUseHfo ?? true);
        setNumberOfAuxBoilers(numberOfAuxBoilers);
        setHasCompositeBoiler(hasCompositeBoiler);
        setCanProduceFreshWaterWithSteam(vessel?.vesselConfigurationV3!.canProduceFreshWaterWithSteam ?? false);
    };

    const onInputChange = (changeObject: InputChangeData) => {
        const name = changeObject.event.target.name;
        const value = changeObject.event.target.type === 'checkbox' ? changeObject.event.target.checked : changeObject.event.target.value;

        if (name === 'boilersConsumptionForOtherText') {
            changeObject.dataObject[name] = value ?? undefined;
        } else if (changeObject.event.target.type === 'checkbox') {
            changeObject.dataObject[name] = value;
        } else if (name === 'consumptionByUsageV5') {
            changeObject.dataObject.boilerConsumptionV5.find(
                (x) => x.BoilersUsageType === changeObject.extraArgs.consumptionType && x.FuelTypeId === changeObject.extraArgs.fuelType
            )!.QuantityMt = value ? +value : 0;
            checkConsumptionSumV5(changeObject.extraArgs.fuelType);
            if (changeObject.extraArgs.consumptionType === BoilersUsageTypeEnum.TankPreparation) {
                setIsBoilerConsumptionTankPreparationIncorrect(checkBoilerConsumptionTankPreparation());
            }
        } else {
            changeObject.dataObject[name] = value ? +value : undefined;
        }

        if (name === 'isSurplusSteamUsedForFreshWaterProduction') {
            if (value) {
                setIsSurplusSteamUsedForFreshWaterProduction(true);
            } else {
                setIsSurplusSteamUsedForFreshWaterProduction(false);
            }
        }
        const voyageFuelPrice = getVoyageFuelPrice(aolDataItem);

        if (name === 'boilersConsumptionHfo') {
            changeObject.dataObject.boilersConsumptionHfoCost = getConsumptionCost(FuelType.HFO, voyageFuelPrice, +value);
        } else if (name === 'boilersConsumptionMgo') {
            changeObject.dataObject.boilersConsumptionMgoCost = getConsumptionCost(FuelType.LGO, voyageFuelPrice, +value);
        } else if (name === 'boilersConsumptionForTankCleaning' || name === 'boilersConsumptionForTankCleaningMgo') {
            setIsBoilerConsumptionTankPreparationIncorrect(checkBoilerConsumptionTankPreparation());
        } else if (name.indexOf('boilersConsumption') !== -1) {
            setIsBoilerConsumptionHFOSumWrong(checkConsumptionSum('hfo'));
            setIsBoilerConsumptionMGOSumWrong(checkConsumptionSum('mgo'));
        } else if (name === 'consumptionMt') {
            if (changeObject.dataObject.consumptionMt === undefined) changeObject.dataObject.consumptionMt = 0;
            changeObject.dataObject.cost = getConsumptionCostByFuelType(changeObject.dataObject as Consumption, voyageFuelPrice);
            checkConsumptionSumV5(changeObject.dataObject.fuelType);
        }

        resetHasEmptyValues();
        hasInvalidFields(enginesDataVM, name, changeObject.isValid);
        onChange();
        checkRunningHours();
    };

    const setIsSurplusSteamUsedForFreshWaterProduction = (value: boolean) => {
        if (!aolDataItem) return;

        aolDataItem.aolDataItemProperties.isSurplusSteamUsedForFreshWaterProduction = value;
    };

    const showOtherText = () => {
        if (aolDataItem.aolVersion >= 5) {
            return enginesDataVM.boilerConsumptionV5?.some((x) => x.BoilersUsageType === BoilersUsageTypeEnum.Other && x.QuantityMt > 0);
        }
        return (
            enginesDataVM.boilersConsumptionForOther !== undefined &&
            (enginesDataVM.boilersConsumptionForOther > 0 || enginesDataVM.boilersConsumptionForOtherMgo)
        );
    };

    const onTimeInputChange = (changeObject: TimeInputChangeData) => {
        changeObject.dataObject[changeObject.name] = changeObject.hours;
        resetHasEmptyValues();
        hasInvalidFields(enginesDataVM, changeObject.name, changeObject.isValid);
        onChange();
        checkRunningHours();
    };

    const resetHasEmptyValues = () => {
        if (hasEmptyValuesLocal) setHasEmptyValuesLocal(false);
    };

    const checkOnSave = () => {
        const ignoreList: string[] = ['consumptionHfoCost', 'consumptionMgoCost'];
        const boilerData = {
            boilersConsumptionHfo: enginesDataVM.boilersConsumptionHfo,
            boilersConsumptionMgo: enginesDataVM.boilersConsumptionMgo,
            boilersData: enginesDataVM.boilersData,
            boilersConsumptionForEngineRoomAndAccommodationHfo: enginesDataVM.boilersConsumptionForEngineRoomAndAccommodation,
            boilersConsumptionForEngineRoomAndAccommodationMgo: enginesDataVM.boilersConsumptionForEngineRoomAndAccommodationMgo,
            boilersConsumptionForTankCleaningHfo: enginesDataVM.boilersConsumptionForTankCleaning,
            boilersConsumptionForTankCleaningMgo: enginesDataVM.boilersConsumptionForTankCleaningMgo,
            boilersConsumptionForCargoHeatingHfo: enginesDataVM.boilersConsumptionForCargoHeating,
            boilersConsumptionForCargoHeatingMgo: enginesDataVM.boilersConsumptionForCargoHeatingMgo,
            boilersConsumptionForFreshWaterProductionHfo: enginesDataVM.boilersConsumptionForFreshWaterProduction,
            boilersConsumptionForFreshWaterProductionMgo: enginesDataVM.boilersConsumptionForFreshWaterProductionMgo,
            boilersConsumptionForOtherHfo: enginesDataVM.boilersConsumptionForOther,
            boilersConsumptionForOtherMgo: enginesDataVM.boilersConsumptionForOtherMgo,
            boilersConsumptionForOtherText: enginesDataVM.boilersConsumptionForOtherText,
        };

        if (!canUseHfo) {
            ignoreList.push('boilersConsumptionHfo');
            ignoreList.push('boilersConsumptionForEngineRoomAndAccommodationHfo');
            ignoreList.push('boilersConsumptionForTankCleaningHfo');
            ignoreList.push('boilersConsumptionForCargoHeatingHfo');
            ignoreList.push('boilersConsumptionForFreshWaterProductionHfo');
            ignoreList.push('boilersConsumptionForOtherHfo');
        }
        if (!showOtherText()) {
            ignoreList.push('boilersConsumptionForOtherText');
        }

        if (!canProduceFreshWaterWithSteam) {
            ignoreList.push('isSurplusSteamUsedForFreshWaterProduction');
        }

        let hasMissing = hasMissingValues(boilerData, ignoreList);
        if (!hasMissing) {
            if (!hasMissing && numberOfAuxBoilers <= 1) {
                hasMissing = hasMissingValues(boilerData.boilersData![0], ignoreList);
            }
            if (!hasMissing && numberOfAuxBoilers <= 2) {
                hasMissing = hasMissingValues(boilerData.boilersData![1], ignoreList);
            }
            if (!hasMissing && hasCompositeBoiler) {
                hasMissing = hasMissingValues(boilerData.boilersData![2], ignoreList);
            }
        }

        setIsBoilerConsumptionMGOSumWrong(checkConsumptionSum('mgo'));
        if (canUseHfo) {
            setIsBoilerConsumptionHFOSumWrong(checkConsumptionSum('hfo'));
        }

        if (hasMissing) {
            setHasEmptyValuesLocal(true);
            hasEmptyValues.current = true;
        } else if (isBoilerConsumptionHFOSumWrong || isBoilerConsumptionMGOSumWrong || invalidBoilerConsumptionByUsage.length > 0) {
            hasEmptyValues.current = true;
        } else {
            if (hasEmptyValuesLocal) setHasEmptyValuesLocal(false);
            hasEmptyValues.current = false;
        }

        checkRunningHours();
    };

    const checkBoilerConsumptionTankPreparation = (): boolean => {
        if (aolDataItem.aolVersion >= 5) {
            const sum = enginesDataVM.boilerConsumptionV5
                ?.filter((x) => x.BoilersUsageType === BoilersUsageTypeEnum.TankPreparation)
                ?.reduce((acc, x) => acc + x.QuantityMt, 0);
            return sum > 0 && !(aolDataItem?.aolDataItemProperties?.hasTankPreparationAolData ?? false);
        } else {
            let sum = getNumberValue(enginesDataVM.boilersConsumptionForTankCleaning, undefined, true)!;
            sum += getNumberValue(enginesDataVM.boilersConsumptionForTankCleaningMgo, undefined, true)!;
            return sum > 0 && !(aolDataItem?.aolDataItemProperties?.hasTankPreparationAolData ?? false);
        }
    };

    const checkConsumptionSum = (type: 'hfo' | 'mgo'): boolean => {
        let sum = 0;
        if (type === 'hfo') {
            sum += getNumberValue(enginesDataVM.boilersConsumptionForEngineRoomAndAccommodation, undefined, true)!;
            sum += getNumberValue(enginesDataVM.boilersConsumptionForTankCleaning, undefined, true)!;
            sum += getNumberValue(enginesDataVM.boilersConsumptionForCargoHeating, undefined, true)!;
            sum += getNumberValue(enginesDataVM.boilersConsumptionForFreshWaterProduction, undefined, true)!;
            sum += getNumberValue(enginesDataVM.boilersConsumptionForOther, undefined, true)!;
        }
        if (type === 'mgo') {
            sum += getNumberValue(enginesDataVM.boilersConsumptionForEngineRoomAndAccommodationMgo, undefined, true)!;
            sum += getNumberValue(enginesDataVM.boilersConsumptionForTankCleaningMgo, undefined, true)!;
            sum += getNumberValue(enginesDataVM.boilersConsumptionForCargoHeatingMgo, undefined, true)!;
            sum += getNumberValue(enginesDataVM.boilersConsumptionForFreshWaterProductionMgo, undefined, true)!;
            sum += getNumberValue(enginesDataVM.boilersConsumptionForOtherMgo, undefined, true)!;
        }

        if (type === 'hfo') {
            return roundTo2Decimals(enginesDataVM.boilersConsumptionHfo ?? 0) !== roundTo2Decimals(sum ?? 0);
        }
        if (type === 'mgo') {
            return roundTo2Decimals(enginesDataVM.boilersConsumptionMgo ?? 0) !== roundTo2Decimals(sum ?? 0);
        }

        return false;
    };

    const checkConsumptionSumV5 = (fuelType: FuelType): boolean => {
        const consumptionSum = enginesDataVM.consumption
            ?.filter((c) => c.engineDataType === EngineDataTypeEnum.Boilers && c.fuelType === fuelType)
            ?.reduce((acc, c) => acc + c.consumptionMt, 0);
        let sum = 0;
        enginesDataVM.boilerConsumptionV5.forEach((x) => {
            if (x.FuelTypeId === fuelType) {
                sum += x.QuantityMt;
            }
        });
        const isFuelTypeConsInvalid = roundTo2Decimals(sum) !== roundTo2Decimals(consumptionSum);

        const invalidConsByUsage = [...invalidBoilerConsumptionByUsage];
        if (isFuelTypeConsInvalid) {
            if (!invalidConsByUsage.includes(fuelType)) {
                invalidConsByUsage.push(fuelType);
            }
        } else {
            const index = invalidConsByUsage.indexOf(fuelType);
            if (index !== -1) {
                invalidConsByUsage.splice(index, 1);
            }
        }
        setInvalidBoilerConsumptionByUsage(invalidConsByUsage);

        return isFuelTypeConsInvalid;
    };

    const checkRunningHours = () => {
        let totRunningHours = 0;
        enginesDataVM.boilersData?.forEach((boilerData) => {
            if (boilerData?.runningHours) {
                totRunningHours += boilerData.runningHours;
            }
        });

        let hasInconsistency = false;
        if (
            aolDataItem.aolVersion >= 5 &&
            totRunningHours === 0 &&
            enginesDataVM.consumption?.some(
                (consumption) => consumption.engineDataType === EngineDataTypeEnum.Boilers && consumption.consumptionMt > 0
            )
        ) {
            hasInconsistency = true;
        } else if (
            aolDataItem.aolVersion < 5 &&
            totRunningHours === 0 &&
            ((enginesDataVM.boilersConsumptionHfo && enginesDataVM.boilersConsumptionHfo > 0) ||
                (enginesDataVM.boilersConsumptionMgo && enginesDataVM.boilersConsumptionMgo > 0))
        ) {
            hasInconsistency = true;
        }

        setIsRunningHoursInconsistent(hasInconsistency);
        hasEmptyValues.current = hasInconsistency;
    };

    return (
        <>
            <SectionInnerWrapper>
                <SubSectionHeader>Boilers</SubSectionHeader>
                {isRunningHoursInconsistent && <ErrorText>Running hours cannot be 0</ErrorText>}
                <EngineListWrapper>
                    <EngineListRow>
                        <div>Running hours</div>
                        <EngineListValuesWrapper gridCols={hasCompositeBoiler ? numberOfAuxBoilers + 1 : numberOfAuxBoilers}>
                            {numberOfAuxBoilers >= 1 && enginesDataVM.boilersData && (
                                <>
                                    <OdfjellInputComponent
                                        isReadOnly={isReadOnly}
                                        type={'time'}
                                        value={enginesDataVM.boilersData[0]?.runningHours}
                                        onTimeInputChange={onTimeInputChange}
                                        name={'runningHours'}
                                        dataObject={enginesDataVM.boilersData[0]}
                                        max={roundTo2Decimals(aolDataItem.reportHours)}
                                        title={'Boiler 1'}
                                        description={'Boil.1:'}
                                    ></OdfjellInputComponent>
                                </>
                            )}
                            {numberOfAuxBoilers >= 2 && enginesDataVM.boilersData && (
                                <OdfjellInputComponent
                                    isReadOnly={isReadOnly}
                                    type={'time'}
                                    value={enginesDataVM.boilersData[1]?.runningHours}
                                    onTimeInputChange={onTimeInputChange}
                                    name={'runningHours'}
                                    dataObject={enginesDataVM.boilersData[1]}
                                    max={roundTo2Decimals(aolDataItem.reportHours)}
                                    title={'Boiler 2'}
                                    description={'Boil.2:'}
                                ></OdfjellInputComponent>
                            )}
                            {hasCompositeBoiler && enginesDataVM.boilersData && (
                                <OdfjellInputComponent
                                    isReadOnly={isReadOnly}
                                    type={'time'}
                                    value={enginesDataVM.boilersData[2]?.runningHours}
                                    onTimeInputChange={onTimeInputChange}
                                    name={'runningHours'}
                                    dataObject={enginesDataVM.boilersData[2]}
                                    max={roundTo2Decimals(aolDataItem.reportHours)}
                                    title={'Composite boiler'}
                                    description={'Comp.:'}
                                ></OdfjellInputComponent>
                            )}
                        </EngineListValuesWrapper>
                    </EngineListRow>
                </EngineListWrapper>
                <EngineListWrapper>
                    {aolDataItem.aolVersion >= 5 ? (
                        <ConsumptionContainer
                            aolItem={aolDataItem}
                            engineType={EngineDataTypeEnum.Boilers}
                            consumption={enginesDataVM?.consumption}
                            isReadOnly={isReadOnly}
                            onChange={onInputChange}
                            addConsumptionRow={addConsumptionRow}
                        />
                    ) : (
                        <>
                            {canUseHfo && (
                                <OdfjellInputComponent
                                    isReadOnly={isReadOnly}
                                    type={'number'}
                                    value={enginesDataVM.boilersConsumptionHfo}
                                    description={'HFO'}
                                    unit={'MT'}
                                    dataObject={enginesDataVM}
                                    cost={enginesDataVM.boilersConsumptionHfoCost}
                                    onChange={onInputChange}
                                    name={'boilersConsumptionHfo'}
                                    min={0}
                                    max={100}
                                />
                            )}

                            <OdfjellInputComponent
                                isReadOnly={isReadOnly}
                                type={'number'}
                                value={enginesDataVM.boilersConsumptionMgo}
                                description={'MGO'}
                                unit={'MT'}
                                dataObject={enginesDataVM}
                                cost={enginesDataVM.boilersConsumptionMgoCost}
                                onChange={onInputChange}
                                name={'boilersConsumptionMgo'}
                                min={0}
                                max={100}
                            />
                        </>
                    )}
                </EngineListWrapper>
            </SectionInnerWrapper>
            <SectionInnerWrapper>
                <SubSectionHeader>Estimated boilers consumption by usage type</SubSectionHeader>
                <EngineListWrapper>
                    <ListHeaderStyle>
                        <EngineListRow>
                            <EngineListHeader>Usage type</EngineListHeader>
                            <EngineListValuesWrapper gridCols={aolDataItem.aolVersion >= 5 ? consumptionTypes.length : 2}>
                                {aolDataItem.aolVersion >= 5 ? (
                                    consumptionTypes.map((type) => (
                                        <EngineListHeader key={`boiler_cons_type_${type}`}>{FuelType[type]}</EngineListHeader>
                                    ))
                                ) : (
                                    <>
                                        {canUseHfo && <EngineListHeader>HFO</EngineListHeader>}
                                        <EngineListHeader>MGO</EngineListHeader>
                                    </>
                                )}
                            </EngineListValuesWrapper>
                        </EngineListRow>
                    </ListHeaderStyle>
                    <EngineListRow>
                        <div>Engine room & accommodation</div>
                        <ConsumptionByUsage
                            canUseHfo={canUseHfo}
                            isReadOnly={isReadOnly}
                            enginesDataVM={enginesDataVM}
                            onInputChange={onInputChange}
                            name={'EngineRoomAndAccommodation'}
                            aolVersion={aolDataItem.aolVersion}
                            fuelTypes={consumptionTypes}
                            consumptionType={BoilersUsageTypeEnum.EngineRoomAndAccommodation}
                        />
                    </EngineListRow>
                    <EngineListRow>
                        <div>Tank preparation</div>
                        <ConsumptionByUsage
                            canUseHfo={canUseHfo}
                            isReadOnly={isReadOnly}
                            enginesDataVM={enginesDataVM}
                            onInputChange={onInputChange}
                            name={'TankCleaning'}
                            aolVersion={aolDataItem.aolVersion}
                            fuelTypes={consumptionTypes}
                            consumptionType={BoilersUsageTypeEnum.TankPreparation}
                        />
                    </EngineListRow>
                    {isBoilerConsumptionTankPreparationIncorrect && (
                        <ErrorText>
                            The estimated boilers consumption for tank preparation should only be registered in combination with the tank preparation
                            section.
                        </ErrorText>
                    )}
                    <EngineListRow>
                        <div>Cargo heating</div>
                        <ConsumptionByUsage
                            canUseHfo={canUseHfo}
                            isReadOnly={isReadOnly}
                            enginesDataVM={enginesDataVM}
                            onInputChange={onInputChange}
                            name={'CargoHeating'}
                            aolVersion={aolDataItem.aolVersion}
                            fuelTypes={consumptionTypes}
                            consumptionType={BoilersUsageTypeEnum.CargoHeating}
                        />
                    </EngineListRow>
                    <EngineListRow>
                        <div>FW production</div>
                        <ConsumptionByUsage
                            canUseHfo={canUseHfo}
                            isReadOnly={isReadOnly}
                            enginesDataVM={enginesDataVM}
                            onInputChange={onInputChange}
                            name={'FreshWaterProduction'}
                            aolVersion={aolDataItem.aolVersion}
                            fuelTypes={consumptionTypes}
                            consumptionType={BoilersUsageTypeEnum.FreshWaterProduction}
                        />
                    </EngineListRow>
                    <EngineListRow>
                        <div>Other</div>
                        <ConsumptionByUsage
                            canUseHfo={canUseHfo}
                            isReadOnly={isReadOnly}
                            enginesDataVM={enginesDataVM}
                            onInputChange={onInputChange}
                            name={'Other'}
                            aolVersion={aolDataItem.aolVersion}
                            fuelTypes={consumptionTypes}
                            consumptionType={BoilersUsageTypeEnum.Other}
                        />
                    </EngineListRow>
                    {isBoilerConsumptionHFOSumWrong && (
                        <ErrorText>The estimated boilers HFO consumption should be equal to the registered HFO boilers consumption.</ErrorText>
                    )}
                    {isBoilerConsumptionMGOSumWrong && (
                        <ErrorText>The estimated boilers MGO consumption should be equal to the registered MGO boilers consumption.</ErrorText>
                    )}
                    {invalidBoilerConsumptionByUsage.length > 0 &&
                        invalidBoilerConsumptionByUsage.map((fuelType) => (
                            <ErrorText key={fuelType}>
                                The estimated boilers consumption for {FuelType[fuelType]} should be equal to the registered boilers consumption.
                            </ErrorText>
                        ))}
                </EngineListWrapper>
                {!!showOtherText() && (
                    <EngineListRow>
                        <div>If other, specify</div>
                        <EngineListValuesWrapper gridCols={1}>
                            <OdfjellInputComponent
                                isReadOnly={isReadOnly}
                                type={'text'}
                                value={enginesDataVM.boilersConsumptionForOtherText}
                                dataObject={enginesDataVM}
                                onChange={onInputChange}
                                name={'boilersConsumptionForOtherText'}
                            />
                        </EngineListValuesWrapper>
                    </EngineListRow>
                )}
            </SectionInnerWrapper>
            {
                <SectionInnerWrapper>
                    <SubSectionHeader>Steam</SubSectionHeader>
                    <EngineListWrapper>
                        <OdfjellInputComponent
                            isReadOnly={isReadOnly}
                            type={'checkbox'}
                            description={'Surplus steam available'}
                            dataObject={enginesDataVM}
                            name={'isSurplusSteamAvailable'}
                            value={enginesDataVM.isSurplusSteamAvailable}
                            onChange={onInputChange}
                        />

                        {canProduceFreshWaterWithSteam && (
                            <OdfjellInputComponent
                                isReadOnly={isReadOnly}
                                type={'checkbox'}
                                description={'Surplus steam used for FW production'}
                                dataObject={enginesDataVM}
                                name={'isSurplusSteamUsedForFreshWaterProduction'}
                                value={enginesDataVM.isSurplusSteamUsedForFreshWaterProduction}
                                onChange={onInputChange}
                            />
                        )}
                    </EngineListWrapper>
                </SectionInnerWrapper>
            }
            {hasEmptyValuesLocal && <ErrorText>You must enter all boiler data before saving.</ErrorText>}
        </>
    );
};

export default BoilersSectionComponent;
